import { Fragment } from "react";
import {
  ArrowRight, Wrench, FileArrowUp, UserCheck, Briefcase, Database, ChartLineUp, Info, Warning,
} from "@phosphor-icons/react";

// Public, no-login work instruction for the NPD Tooling Budget flow (user's
// ask, Jun 2026: "create a basic instructions flow url for requesting approval
// > approval 1 and 2 and then how it becomes visible in spend section").
// Reached through AppShell's auth bypass at /docs/tooling-budget-flow.

const FLOW = [
  { label: "NPD raises", sub: "amount + quotation", icon: FileArrowUp },
  { label: "NPD Head", sub: "approval 1", icon: UserCheck },
  { label: "Management", sub: "approval 2", icon: Briefcase },
  { label: "SAP", sub: "planned cost on the task", icon: Database },
  { label: "Project Spend", sub: "visible per project", icon: ChartLineUp },
];

const STEPS = [
  {
    number: 1,
    title: "NPD raises the tooling budget",
    who: "NPD team",
    where: "NPD \u2192 Tooling Budget Requests",
    icon: FileArrowUp,
    body: [
      "Pick the SAP project, then the project task the tool belongs to. SAP books cost on a task, never on the project header - this is the task the budget will sit on.",
      "Enter the amount in rupees, say what the tooling is for, and attach the quotation or costing sheet. At least one file is required; add as many as you need (PDF, image, Word or Excel, up to 15 MB each).",
      "\"Send for Approval\" is only enabled once all of those are filled - the hint under the button always names what is still missing.",
    ],
    example: "Example: \u20B92,40,000 for a progressive die on COC10 \u2192 task COC10-T4, with the vendor quote attached.",
    shots: [
      { src: "/tooling-guide/t1_raise_form.jpg", caption: "NPD \u2192 Tooling Budget Requests: project, task, amount, purpose and the quotation. The button stays off until all of them are filled." },
    ],
  },
  {
    number: 2,
    title: "Approval 1 - NPD Head",
    who: "whoever holds the NPD Head approval right",
    where: "NPD \u2192 Tooling Budget Approvals",
    icon: UserCheck,
    body: [
      "The head gets an email the moment the request is raised, and the request appears under \"Waiting on me\".",
      "Three choices: approve the full amount, approve a LOWER amount (a reason is then required), or reject with a remark. Nothing can be approved above what was asked for.",
      "Approving passes it to management. Rejecting ends it there, and the NPD team sees the remark on the request.",
    ],
    example: "Example: the head sanctions \u20B92,00,000 of the \u20B92,40,000 with the reason \"second vendor quoted lower\".",
    shots: [
      { src: "/tooling-guide/t2_head_queue.jpg", caption: "With \u201CWaiting on me\u201D ticked, the head sees only the requests at their own stage." },
      { src: "/tooling-guide/t3_approve_dialog.jpg", narrow: true, caption: "Approving: the amount can be lowered - and a reason becomes mandatory the moment it is. Management later sees the same box with their own limit." },
    ],
  },
  {
    number: 3,
    title: "Approval 2 - Management",
    who: "whoever holds the Management approval right",
    where: "NPD \u2192 Tooling Budget Approvals",
    icon: Briefcase,
    body: [
      "Management is emailed once the head has passed it on, and sees it under \"Waiting on me\".",
      "Same three choices, with one limit: management can never sanction more than the head let through. Cutting it further again needs a reason.",
      "Management's figure is the final sanctioned budget.",
    ],
    example: "Example: management approves the \u20B92,00,000 that reached them - that becomes the sanctioned budget.",
    shots: [
      { src: "/tooling-guide/t4_in_sap.jpg", caption: "Every decision stays on the request: who approved, how much and why it was changed." },
    ],
  },
  {
    number: 4,
    title: "The sanctioned amount goes into SAP",
    who: "the app, automatically",
    where: "SAP \u2192 the project task's Expenses plan",
    icon: Database,
    body: [
      "The moment management approves, the app writes the sanctioned amount onto that project task as planned cost, under the expense group \"Job Work on Die & tools\", with the request number in the description.",
      "It is written once per request. If SAP refuses, the approval still stands and the request shows the SAP error with a \"Send budget to SAP\" button for management to try again.",
      "Actual money spent fills in from real documents - the purchase of the tool or material consumption. The app never invents actual cost.",
    ],
    example: "Example: COC10-T4 now carries \u20B92,00,000 of planned cost in SAP, with actual cost still \u20B90 until the tool is invoiced.",
    shots: [
      { src: "/tooling-guide/t4b_sap_line.jpg", caption: "The green line is the app confirming what SAP accepted - amount, project task and expense group." },
    ],
  },
  {
    number: 5,
    title: "Where it shows up - Project Spend",
    who: "anyone on the NPD page",
    where: "NPD \u2192 Store Requests and Approvals \u2192 Project Spend",
    icon: ChartLineUp,
    body: [
      "Each project carries a \"Tooling budgets (all time)\" block: Requested, Awaiting approval and Sanctioned side by side, plus every request with its task, who raised it, the amounts and its stage.",
      "The headline shows the material booked for the month next to the tooling sanctioned so far. The two are kept apart on purpose: material is valued by SAP for the month you pick, tooling is the sanctioned figure and runs all-time, because a tool is sanctioned once and spent over however long it takes.",
      "Projects with a tooling budget but no material movement that month still appear in the list.",
    ],
    shots: [
      { src: "/tooling-guide/t5_project_spend.jpg", caption: "NPD \u2192 Store Requests and Approvals \u2192 Project Spend: open a project to see its tooling budgets and every request behind them." },
    ],
  },
];

const RULES = [
  "A request cannot be raised without a project, a task, an amount and at least one file.",
  "Nobody can sanction more than the amount that reached them - the head sees what NPD asked for, management sees what the head passed on.",
  "Sanctioning less than what reached you always needs a reason; rejecting always needs a remark.",
  "Only the approver whose turn it is can act - the other stage's buttons are not even shown.",
  "An administrator can act at either stage; the approval rights exist so finance or management can approve WITHOUT admin rights.",
];

const Pill = ({ tone, children }) => (
  <span className={`inline-block text-[11px] font-bold uppercase tracking-wide border rounded-sm px-1.5 py-0.5 ${tone}`}>{children}</span>
);

export default function ToolingBudgetGuidePage() {
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="tooling-guide-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center px-4 sm:px-6">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-white/15 flex items-center justify-center">
            <Wrench size={18} className="text-white" weight="fill" />
          </div>
          <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control &middot; Work Instruction</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <p className="font-data text-xs font-bold text-[#0E7C86] uppercase tracking-widest mb-2" data-testid="guide-doc-code">SOP-NPD-03 &middot; NPD Tooling Budget</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] tracking-tight" data-testid="guide-title">
            Tooling Budget: request, two approvals, and where the cost appears
          </h1>
          <p className="text-base text-[#475569] mt-3 max-w-3xl leading-relaxed">
            How a tooling cost travels from an NPD request to the project's expenditure - who does what, what each
            approver can change, and where the money shows up afterwards.
          </p>
        </div>

        {/* The whole journey at a glance */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-5 mb-10 shadow-sm" data-testid="guide-flow-strip">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            {FLOW.map((f, i) => {
              const Icon = f.icon;
              return (
                <Fragment key={f.label}>
                  <div className="sm:flex-1 w-full bg-[#F8FAFC] border border-[#EAECF0] rounded-lg px-3 py-3 text-center">
                    <Icon size={20} className="text-[#0E7C86] mx-auto mb-1.5" weight="fill" />
                    <p className="font-heading text-[13px] font-bold text-[#101828] leading-tight">{f.label}</p>
                    <p className="text-[11px] text-[#667085] mt-0.5">{f.sub}</p>
                  </div>
                  {i < FLOW.length - 1 && (
                    <ArrowRight size={18} className="text-[#94A3B8] shrink-0 mx-auto rotate-90 sm:rotate-0" />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>

        <div className="flex items-start gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4 mb-10" data-testid="guide-access-banner">
          <Info size={20} className="text-[#1E40AF] shrink-0 mt-0.5" weight="fill" />
          <div className="text-sm text-[#1E40AF] leading-relaxed">
            <p className="font-semibold mb-1">Who can see what</p>
            <ul className="space-y-0.5">
              <li>&middot; <strong>NPD team</strong> (NPD Store Request right): raises budgets and tracks their own.</li>
              <li>&middot; <strong>NPD Head / Management</strong> (their own approval rights, ticked in IT Access Management): see only the Approvals screen.</li>
              <li>&middot; Ask IT for the right you need - no admin rights are required to approve.</li>
            </ul>
          </div>
        </div>

        <ol className="space-y-6">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <li key={step.number} data-testid={`guide-step-${step.number}`}>
                <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm">
                  <div className="flex flex-wrap items-center gap-3 px-5 sm:px-6 py-4 border-b border-[#EAECF0] bg-[#F8FAFC]">
                    <div className="w-9 h-9 rounded-full bg-[#0E7C86] text-white font-heading font-bold text-sm flex items-center justify-center shrink-0">
                      {step.number}
                    </div>
                    <Icon size={18} className="text-[#0E7C86] shrink-0" />
                    <h2 className="font-heading text-lg font-semibold text-[#101828] tracking-tight" data-testid={`guide-step-${step.number}-title`}>
                      {step.title}
                    </h2>
                    <span className="ml-auto flex flex-wrap items-center gap-2">
                      <Pill tone="bg-[#F2F4F7] text-[#475467] border-[#EAECF0]">{step.who}</Pill>
                      <Pill tone="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">{step.where}</Pill>
                    </span>
                  </div>
                  <div className="p-5 sm:p-6 space-y-3">
                    {step.body.map((line, i) => (
                      <p key={i} className="text-sm text-[#475569] leading-relaxed">{line}</p>
                    ))}
                    {step.example && (
                      <p className="text-[13px] text-[#0E7C86] bg-[#F0FDFA] border border-[#CCFBF1] rounded-md px-3 py-2">
                        {step.example}
                      </p>
                    )}

                    {/* Real screenshots of the app, so the page mirrors what
                        users actually see (user's ask, Jun 2026). Click opens
                        the full-size image. */}
                    {(step.shots || []).map((shot) => (
                      <figure key={shot.src} className={`mt-3 ${shot.narrow ? "max-w-[520px]" : ""}`} data-testid={`guide-shot-${step.number}`}>
                        <a href={shot.src} target="_blank" rel="noreferrer" className="block border border-[#D0D5DD] rounded-lg overflow-hidden bg-[#F8FAFC] hover:border-[#0E7C86] transition-colors">
                          <img src={shot.src} alt={shot.caption} loading="lazy" className="w-full block" />
                        </a>
                        <figcaption className="text-[12px] text-[#667085] mt-1.5 leading-relaxed">
                          {shot.caption}
                          <span className="sm:hidden text-[#0E7C86] font-bold"> Tap to enlarge.</span>
                        </figcaption>
                      </figure>
                    ))}

                  </div>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="flex justify-center py-1">
                    <ArrowRight size={20} className="text-[#94A3B8] rotate-90" />
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        <div className="mt-10 bg-white border border-[#E2E8F0] rounded-xl p-5 sm:p-6 shadow-sm" data-testid="guide-rules">
          <div className="flex items-center gap-2 mb-3">
            <Warning size={18} className="text-[#B54708]" weight="fill" />
            <h2 className="font-heading text-lg font-semibold text-[#101828]">The rules the app enforces</h2>
          </div>
          <ul className="space-y-2">
            {RULES.map((r, i) => (
              <li key={i} className="text-sm text-[#475569] leading-relaxed flex gap-2">
                <span className="text-[#0E7C86] font-bold">&middot;</span>{r}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-[12px] text-[#98A2B3] mt-8 text-center" data-testid="guide-footer-note">
          Amounts are in Indian rupees. Share this page with anyone - it opens without a login.
        </p>
      </main>
    </div>
  );
}
